import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Box,
  Grid,
  Tooltip,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CleaningServices as CleaningServicesIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  FolderOpen as FolderOpenIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { diskApi } from '../../api/disk.api';

const DiskManager = () => {
  const [diskUsage, setDiskUsage] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ 
    open: false, 
    logPath: '', 
    logName: '' 
  });
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [cleaning, setCleaning] = useState(false);
  const [rotating, setRotating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usageRes, logsRes] = await Promise.all([
        diskApi.getDiskUsage(),
        diskApi.getLogs()
      ]);
      
      setDiskUsage(usageRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching disk data:', error);
      showSnackbar(
        error.response?.data?.detail || 'Error al cargar datos del disco', 
        'error'
      );
    }
    setLoading(false);
  };

  const handleDeleteLog = async () => {
    const { logPath } = confirmDialog;
    setDeleting(logPath);
    try {
      await diskApi.deleteLog(logPath);
      showSnackbar('Log eliminado correctamente', 'success');
      fetchData();
    } catch (error) {
      showSnackbar(
        error.response?.data?.detail || 'Error al eliminar log', 
        'error'
      );
    }
    setDeleting(null);
    setConfirmDialog({ open: false, logPath: '', logName: '' });
  };

  const handleCleanOldLogs = async () => {
    if (!window.confirm('¿Estás seguro de eliminar todos los logs con más de 30 días?')) {
      return;
    }
    
    setCleaning(true);
    try {
      const response = await diskApi.cleanOldLogs(30);
      showSnackbar(
        `✅ Se eliminaron ${response.data.files_deleted} archivos. Espacio liberado: ${response.data.freed_space}`,
        'success'
      );
      fetchData();
    } catch (error) {
      showSnackbar(
        error.response?.data?.detail || 'Error al limpiar logs antiguos', 
        'error'
      );
    }
    setCleaning(false);
  };

  const handleRunLogrotate = async () => {
    setRotating(true);
    try {
      await diskApi.runLogrotate();
      showSnackbar('✅ Logrotate ejecutado correctamente', 'success');
      fetchData();
    } catch (error) {
      showSnackbar(
        error.response?.data?.detail || 'Error al ejecutar logrotate', 
        'error'
      );
    }
    setRotating(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    fetchData();
    // Actualizar cada 60 segundos
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch(status) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      default: return 'success';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'critical': return <WarningIcon color="error" />;
      case 'warning': return <WarningIcon color="warning" />;
      default: return <CheckCircleIcon color="success" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!diskUsage) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestión de Disco
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GavelIcon />}
            onClick={handleRunLogrotate}
            disabled={rotating}
          >
            {rotating ? 'Ejecutando...' : 'Ejecutar Logrotate'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<CleaningServicesIcon />}
            onClick={handleCleanOldLogs}
            disabled={cleaning}
          >
            {cleaning ? 'Limpiando...' : 'Limpiar Logs Antiguos'}
          </Button>
        </Stack>
      </Box>

      {/* Tarjeta de uso de disco */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Espacio en Disco
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="textSecondary">
                Total
              </Typography>
              <Typography variant="h6">{diskUsage.total}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="textSecondary">
                Usado
              </Typography>
              <Typography variant="h6" color={getStatusColor(diskUsage.status)}>
                {diskUsage.used}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="textSecondary">
                Libre
              </Typography>
              <Typography variant="h6" color="success.main">
                {diskUsage.free}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="textSecondary">
                Estado
              </Typography>
              <Chip
                icon={getStatusIcon(diskUsage.status)}
                label={`${diskUsage.percent}%`}
                color={getStatusColor(diskUsage.status)}
                sx={{ fontSize: '1.1rem', py: 2 }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LinearProgress
              variant="determinate"
              value={diskUsage.percent}
              color={getStatusColor(diskUsage.status)}
              sx={{ flex: 1, height: 12, borderRadius: 6 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Tabla de logs */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <FolderOpenIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Logs ({logs.length})
            </Typography>
            {loading && <CircularProgress size={24} />}
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tamaño</TableCell>
                  <TableCell>Modificado</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary" sx={{ py: 2 }}>
                        No hay logs grandes para mostrar
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.path} hover>
                      <TableCell>
                        <Tooltip title={log.path}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {log.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {log.size}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.modified}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.compressed ? (
                          <Chip label="Comprimido" size="small" color="info" />
                        ) : (
                          <Chip label="Activo" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Eliminar este log">
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              disabled={deleting === log.path || log.compressed === false}
                              onClick={() => setConfirmDialog({
                                open: true,
                                logPath: log.path,
                                logName: log.name
                              })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!log.compressed && (
                          <Typography variant="caption" color="textSecondary">
                            (ejecuta logrotate primero)
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Diálogo de confirmación */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={() => setConfirmDialog({ open: false, logPath: '', logName: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de eliminar el log <strong>{confirmDialog.logName}</strong>?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 2, p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
            ⚠️ Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, logPath: '', logName: '' })}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteLog} 
            color="error" 
            variant="contained"
            disabled={deleting === confirmDialog.logPath}
            startIcon={deleting === confirmDialog.logPath ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting === confirmDialog.logPath ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DiskManager;
